<template lang="pug">
div
  //- Header
  v-row(no-gutters align="center")
    v-col
      .yz-heading-8.text-neutral-700 Price tier
      .yz-regular-body-sm.text-neutral-500.mt-1 Add, edit, remove or change status of your price tier.
    v-col.shrink
      yz-btn(label="All branches" outlined size="lg" background-color="white")

  yz-divider.mt-4

  //- Tabs and search
  v-row.mt-4(no-gutters align="center")
    v-col(cols="12" md="8")
      yz-tabs(v-model="activeTab" type="line")
        yz-tab(:label="$t('all')")
        yz-tab(label="Active")
        yz-tab(label="Archived")
    v-col(cols="12" md="4" align="end")
      .d-flex.align-center.justify-end
        yz-search-btn(v-model="searchText" @search="onSearch")
        yz-btn.ml-3(label="Add price tier" size="lg" @click="openPricingTierDialog = true")

  yz-divider

  //- Data table
  yz-data-table.mt-2(:headers="headers" :items="tiers" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" no-outer-border radius="0" disable-sort body-height="52px" header-height="36px" header-background-color="white" @click:row="onEditTier")
    //- Name
    template(v-slot:item.name="{ item }")
      div
        .yz-medium-body-sm.text-neutral-700 {{ item.name }}
        .yz-regular-body-xs.text-neutral-500 {{ item.description }}
    //- Payment methods
    template(v-slot:item.payment="{ item }")
      .d-flex.align-center
        div
          .yz-regular-body-xs.text-neutral-700 Panel: {{ getPanelCount(item.panels) }}
          .yz-regular-body-xs.text-neutral-700.mt-1 Self-Pay: {{ getSelfPayCount(item.self_pay) }}
        yz-btn.ml-3(icon-only icon="mdi-information-outline" icon-size="20" size="lg" @click.stop="onOpenDetails(item)")
    //- Status
    template(v-slot:item.status="{ item }")
      yz-badge(v-if="item.status === 'active'" color="success-100" textColor="success-700")
        v-icon.mr-1(size="8" color="success600") mdi-circle
        | Active
      yz-badge(v-else color="danger-100" textColor="danger-700")
        v-icon.mr-1(size="8" color="danger600") mdi-circle
        | Archived
    //- Branches
    template(v-slot:item.branches)
      yz-badge(border) All branches
    //- Actions
    template(v-slot:item.actions="{ item, index }")
      .d-flex.justify-center(@click.stop)
        v-menu(offset-y :top="menuPositionTop(index)" left rounded="lg")
          template(#activator="{ on }")
            yz-btn(icon-only icon="mdi-dots-horizontal" icon-size="24" size="sm" icon-color="neutral-500" v-on="on")
          yz-list
            yz-list-item(@click="onEditTier(item)") Edit this tier
            yz-divider
            yz-list-item(@click="onChangeStatus(item)")
              span(:class="item.status === 'active' ? 'danger500--text' : 'success600--text'") {{ item.status === 'active' ? 'Archive' : 'Activate' }} this tier
            template(v-if="activeTab === 2")
              yz-divider
              yz-list-item(@click="onDeleteItem(item)")
                span.danger500--text Delete this tier

  //- Dialogs
  pricing-tier-dialog(v-if="openPricingTierDialog" :open.sync="openPricingTierDialog" :parentTier="selectedTier" @closed="onClosed" @reset="onReset")
  pricing-tier-details-dialog(v-if="openPricingTierDetailsDialog" :open.sync="openPricingTierDetailsDialog" :tier="selectedTier" @closed="selectedTier = null")
  delete-confirmation-dialog(v-if="openDeleteDialog" :open.sync="openDeleteDialog" :loading="isLoading" @cancel="onClosed" @delete="onConfirmDelete")
    .yz-heading-8 Are you sure you want to delete this price tier?
    .yz-regular-body-sm.mt-4 This action will be done immediately and can't be undone. Click "Confirm" to proceed
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PricingTierDialog from '@/components/clinic/inventory/PricingTierDialog.vue'
import PricingTierDetailsDialog from '@/components/clinic/inventory/PricingTierDetailsDialog.vue'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'

export default {
  components: { PricingTierDialog, PricingTierDetailsDialog, DeleteConfirmationDialog },
  data: () => ({
    isFetching: false,
    isLoading: false,
    openPricingTierDialog: false,
    openPricingTierDetailsDialog: false,
    openDeleteDialog: false,
    selectedTier: null,
    activeTab: 0,
    pageSize: -1,
    searchText: '',
    tiers: [],
    headers: [
      { text: 'NAME', value: 'name' },
      { text: 'PAYMENT METHODS', value: 'payment' },
      { text: 'STATUS', value: 'status', width: '120', sortable: false },
      { text: 'BRANCHES', value: 'branches', sortable: false },
      { text: 'ACTION', value: 'actions', width: '80', sortable: false, align: 'end' }
    ]
  }),
  computed: {
    ...mapGetters({ company: 'getCompany' })
  },
  watch: {
    activeTab () {
      this.searchText = ''
      this.fetchPricingTiers()
    }
  },
  mounted () {
    this.$emit('update:footer', { show: false })
    this.fetchPricingTiers()
  },
  beforeDestroy () {
    this.$emit('update:footer', { show: false })
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchPricingTiers () {
      this.isFetching = true
      const params = {
        page_size: this.pageSize,
        company: this.company.id
      }
      if (this.activeTab === 1) { params.status = 'active' }
      if (this.activeTab === 2) { params.status = 'archived' }
      if (this.searchText) { params.search = this.searchText }
      this.$axios.get('/product_pricetiers', { params })
        .then(resp => {
          this.tiers = resp.data && resp.data.results ? resp.data.results : []
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    onSearch () {
      this.fetchPricingTiers()
    },
    onChangeStatus (item) {
      this.isFetching = true
      const newStatus = item.status === 'active' ? 'archived' : 'active'
      this.$axios.patch(`/product_pricetiers/${item.id}`, { status: newStatus })
        .then(() => {
          this.onReset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          this.isFetching = false
        })
    },
    onConfirmDelete () {
      this.isLoading = true
      this.$axios.delete(`/product_pricetiers/${this.selectedTier.id}`)
        .then(() => {
          this.onReset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    getPanelCount (panels) {
      const count = panels ? panels.length : 0
      if (count === 0) return 'None included'
      return count + ' included'
    },
    getSelfPayCount (methods) {
      const count = methods ? methods.length : 0
      if (count === 0) return 'None included'
      return count + ' included'
    },
    onEditTier (item) {
      this.selectedTier = item
      this.openPricingTierDialog = true
    },
    onOpenDetails (item) {
      this.selectedTier = item
      this.openPricingTierDetailsDialog = true
    },
    onDeleteItem (item) {
      this.selectedTier = item
      this.openDeleteDialog = true
    },
    onReset () {
      this.fetchPricingTiers()
      this.selectedTier = null
      this.openPricingTierDialog = false
      this.openDeleteDialog = false
    },
    onClosed () {
      this.selectedTier = null
      this.openPricingTierDialog = false
      this.openDeleteDialog = false
    },
    menuPositionTop (index) {
      if (index + 1 > 4 && index + 1 > this.tiers.length - 3) return true
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover > td {
  cursor: pointer;
  background: var(--yz-neutral-200) !important;
}
</style>
